/**
 * Detect baked-in UI checkerboard (e.g. Google Images / browser transparency preview
 * flattened into clipboard pixels) and convert those cells to real transparency.
 *
 * Uses corner quadrants + border fallback + grid phase search. Full edge bands
 * often include the subject (e.g. circle stroke), which breaks two-tone means;
 * corners usually stay pure checker.
 *
 * Debug: in dev, or set `window.__CANVAS_PASTE_DEBUG__ = true` then reload — watch
 * `[Canvas checkerboard]` in the console.
 */

const MAX_PIXELS_TO_PROCESS = 8 * 1000 * 1000;

const pasteDebug = (...args) => {
  try {
    const force =
      typeof window !== "undefined" && window.__CANVAS_PASTE_DEBUG__ === true;
    if (force || process.env.NODE_ENV === "development") {
    }
  } catch {
    /* noop */
  }
};

/** Large periods first (e.g. 800px / 20 cells = 40). */
const PERIODS = [
  40, 32, 36, 48, 30, 28, 24, 20, 16, 14, 12, 10, 8, 6, 7, 5, 4, 3, 2, 9, 11,
  13, 15, 17, 18, 19, 22,
];

/** Detect grid on a downscaled copy; apply strip at full res (much faster on large images). */
const ANALYSIS_MAX_DIM = 448;
/** Stop scanning periods once we have a very confident fit. */
const EARLY_EXIT_MATCH_RATE = 0.88;

const isLightNeutralRgb = (r, g, b) => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max < 1 ? 0 : (max - min) / max;
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return sat < 0.4 && lum > 72;
};

const distRgb = (r, g, b, cx, cy, cz) =>
  Math.hypot(r - cx, g - cy, b - cz);

/** Soft PNG/WebP grids can be ~9+ apart; JPEG false fits gated at strip time. */
const MIN_CHECKER_PAIR_DIST = 9;
/** Do not strip JPEGs on weak two-tone (flat photos / DCT). */
const JPEG_MIN_CHECKER_PAIR_DIST = 14;
/** Each parity bucket should be a flat tile color, not photo texture or noise. */
const MAX_CLUSTER_RMS = 27;
/** When the fit is this strong, strip globally (checker inside icons, not just from edges). */
const GLOBAL_STRIP_MIN_MATCH = 0.58;
/** Require visible two-tone separation before global strip (same floor as detection). */
const GLOBAL_STRIP_MIN_PAIR_DIST = MIN_CHECKER_PAIR_DIST;
/** Looser fits (high scatter) may still be real checker on busy borders — edge flood only. */
const GLOBAL_STRIP_MAX_RMS = 25;

/**
 * @param {"corners" | "border"} region
 */
const scoreCheckerboardRegionFit = (data, w, h, P, ox, oy, region) => {
  const cornerSide = Math.max(
    16,
    Math.min(120, Math.round(Math.min(w, h) * 0.2))
  );
  const border = Math.max(
    4,
    Math.min(Math.floor(Math.min(w, h) * 0.08), 48)
  );

  const inSample = (x, y) => {
    if (region === "corners") {
      return (
        (x < cornerSide && y < cornerSide) ||
        (x >= w - cornerSide && y < cornerSide) ||
        (x < cornerSide && y >= h - cornerSide) ||
        (x >= w - cornerSide && y >= h - cornerSide)
      );
    }
    return (
      x < border || x >= w - border || y < border || y >= h - border
    );
  };

  let minCluster =
    region === "corners"
      ? Math.max(18, Math.floor((cornerSide * cornerSide) / (P * P)))
      : Math.max(10, Math.floor((border * 2 * (w + h)) / (P * P * 4)));

  let sum0r = 0,
    sum0g = 0,
    sum0b = 0,
    n0 = 0;
  let sum1r = 0,
    sum1g = 0,
    sum1b = 0,
    n1 = 0;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!inSample(x, y)) continue;
      const i = (y * w + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 160) continue;
      const parity =
        (Math.floor((x + ox) / P) + Math.floor((y + oy) / P)) & 1;
      if (parity === 0) {
        sum0r += r;
        sum0g += g;
        sum0b += b;
        n0++;
      } else {
        sum1r += r;
        sum1g += g;
        sum1b += b;
        n1++;
      }
    }
  }

  if (n0 < minCluster || n1 < minCluster) return null;

  const c0 = { r: sum0r / n0, g: sum0g / n0, b: sum0b / n0 };
  const c1 = { r: sum1r / n1, g: sum1g / n1, b: sum1b / n1 };

  if (!isLightNeutralRgb(c0.r, c0.g, c0.b) || !isLightNeutralRgb(c1.r, c1.g, c1.b)) {
    return null;
  }

  const pairDist = distRgb(c0.r, c0.g, c0.b, c1.r, c1.g, c1.b);
  if (pairDist < MIN_CHECKER_PAIR_DIST || pairDist > 185) return null;

  const T = region === "corners" ? 42 : 38;
  const minMatchRate = region === "corners" ? 0.58 : 0.6;
  const minTotal = region === "corners" ? 48 : 32;

  let match = 0;
  let total = 0;
  let sse0 = 0;
  let sse1 = 0;
  let sseToCluster = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!inSample(x, y)) continue;
      const i = (y * w + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      if (a < 160) continue;
      const parity =
        (Math.floor((x + ox) / P) + Math.floor((y + oy) / P)) & 1;
      const exp = parity === 0 ? c0 : c1;
      const d = distRgb(r, g, b, exp.r, exp.g, exp.b);
      sseToCluster += d * d;
      if (parity === 0) sse0 += d * d;
      else sse1 += d * d;
      total++;
      if (d < T) match++;
    }
  }

  if (total < minTotal) return null;

  const rms0 = Math.sqrt(sse0 / n0);
  const rms1 = Math.sqrt(sse1 / n1);
  if (rms0 > MAX_CLUSTER_RMS || rms1 > MAX_CLUSTER_RMS) return null;

  const rmsToCluster = Math.sqrt(sseToCluster / total);
  const minPairByScatter = Math.max(6.5, rmsToCluster * 2.55);
  if (pairDist < minPairByScatter) return null;

  const matchRate = match / total;
  if (matchRate < minMatchRate) return null;
  return {
    matchRate,
    c0,
    c1,
    ox,
    oy,
    region,
    rmsToCluster,
    pairDist,
  };
};

const phaseStepForP = (P) => (P <= 48 ? 1 : 2);

const modPositive = (n, m) => ((n % m) + m) % m;

/**
 * Run period scan on analysis-sized pixels; map P/phase back to full image space.
 */
const findBestCheckerboardFitScaled = (dataS, wS, hS, wFull, hFull) => {
  let best = null;
  const perPeriodBest = [];
  for (const pFull of PERIODS) {
    const pS = Math.max(2, Math.round((pFull * wS) / wFull));
    const scored = bestFitForPeriod(dataS, wS, hS, pS);
    if (scored) {
      perPeriodBest.push({
        P: pFull,
        pS,
        matchRate: Number(scored.matchRate.toFixed(3)),
        ox: scored.ox,
        oy: scored.oy,
        region: scored.region,
      });
    }
    if (!scored) continue;
    const mapped = {
      P: pFull,
      c0: scored.c0,
      c1: scored.c1,
      ox: modPositive(Math.round((scored.ox * wFull) / wS), pFull),
      oy: modPositive(Math.round((scored.oy * hFull) / hS), pFull),
      matchRate: scored.matchRate,
      region: scored.region,
      rmsToCluster: scored.rmsToCluster,
      pairDist: scored.pairDist,
    };
    if (!best || mapped.matchRate > best.matchRate) {
      best = mapped;
    }
    if (
      mapped.matchRate >= EARLY_EXIT_MATCH_RATE &&
      mapped.pairDist >= MIN_CHECKER_PAIR_DIST + 1
    ) {
      break;
    }
  }
  return { best, perPeriodBest };
};

/**
 * Try all phase offsets for one period; return best scoring fit.
 */
const bestFitForPeriod = (data, w, h, P) => {
  let best = null;
  const step = phaseStepForP(P);
  for (let ox = 0; ox < P; ox += step) {
    for (let oy = 0; oy < P; oy += step) {
      const corner = scoreCheckerboardRegionFit(
        data,
        w,
        h,
        P,
        ox,
        oy,
        "corners"
      );
      const edge = scoreCheckerboardRegionFit(
        data,
        w,
        h,
        P,
        ox,
        oy,
        "border"
      );
      const pick = (a, b) => {
        if (!a) return b;
        if (!b) return a;
        return a.matchRate >= b.matchRate ? a : b;
      };
      const scored = pick(corner, edge);
      if (!scored) continue;
      if (!best || scored.matchRate > best.matchRate + 0.006) {
        best = scored;
      }
    }
  }
  return best;
};

/**
 * Cheap corner-only screen: skip full detection only for clearly colorful corners.
 * Subtle gray/white UI checkers are *not* skipped (flat luminance heuristics caused false skips).
 * @param {Uint8ClampedArray} data
 * @param {number} w
 * @param {number} h
 * @returns {boolean} true = skip expensive detection (keep original pixels)
 */
const fastSkipCheckerboardDetection = (data, w, h) => {
  const side = Math.max(
    12,
    Math.min(64, Math.round(Math.min(w, h) * 0.1))
  );

  let satSum = 0;
  let n = 0;
  let meaningfulAlpha = false;
  let lumMin = 255;
  let lumMax = 0;

  const sampleBlock = (x0, y0) => {
    for (let y = y0; y < y0 + side && y < h; y++) {
      for (let x = x0; x < x0 + side && x < w; x++) {
        const i = (y * w + x) * 4;
        const a = data[i + 3];
        if (a < 235) {
          meaningfulAlpha = true;
        }
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum < lumMin) lumMin = lum;
        if (lum > lumMax) lumMax = lum;
        const mx = Math.max(r, g, b);
        const mn = Math.min(r, g, b);
        satSum += mx < 1 ? 0 : (mx - mn) / mx;
        n++;
      }
    }
  };

  sampleBlock(0, 0);
  sampleBlock(w - side, 0);
  sampleBlock(0, h - side);
  sampleBlock(w - side, h - side);

  if (n === 0) return false;

  const meanSat = satSum / n;
  const lumSpan = lumMax - lumMin;

  if (meaningfulAlpha) {
    pasteDebug("fastSkip: off — corner pixels have transparency");
    return false;
  }

  // Flat light corners (studio white/grey backdrops, JPEG) are not baked checkerboards;
  // the full checker detector can otherwise fit parity noise as a fake two-tone grid.
  // Tight luminance span = no alternating UI checker in corners (flat white/grey/JPEG noise).
  // Keep span conservative so #eee/#fff style checkers (ΔL often ~12–22) still run detection.
  if (
    meanSat < 0.12 &&
    lumSpan <= 5 &&
    lumMax >= 88 &&
    lumMin >= 78
  ) {
    pasteDebug("fastSkip: extremely flat corners (no visible two-tone)", {
      meanSat: Number(meanSat.toFixed(3)),
      lumSpan: Number(lumSpan.toFixed(1)),
    });
    return true;
  }

  // Only skip on *very* saturated corners. Baked checkers are neutral; subjects
  // (e.g. butterfly) often touch corners and are moderately saturated — those
  // must still run the full detector.
  if (meanSat > 0.52) {
    pasteDebug("fastSkip: highly saturated corners (unlikely UI checker)", {
      meanSat: Number(meanSat.toFixed(3)),
    });
    return true;
  }

  // Do NOT skip on low luminance span alone: subtle checkers (#eee vs #fff) often
  // measure lumSpan 15–22 in a corner patch and were incorrectly skipped.

  return false;
};

/**
 * Clear every pixel that matches the checker cell colors (parity + tolerance).
 * Used when the statistical fit is strong; interior checker behind opaque strokes
 * is not reachable from the image edge by flood-fill alone.
 */
const applyCheckerTransparencyGlobal = (data, w, h, P, ox, oy, c0, c1, tol) => {
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (data[i + 3] < 12) continue;
      const parity =
        (Math.floor((x + ox) / P) + Math.floor((y + oy) / P)) & 1;
      const exp = parity === 0 ? c0 : c1;
      if (
        distRgb(data[i], data[i + 1], data[i + 2], exp.r, exp.g, exp.b) < tol
      ) {
        data[i + 3] = 0;
      }
    }
  }
};

/**
 * Clear checker cells only when 4-connected to the image border through matching
 * cells. Safer for marginal fits (avoids nuking large light interior regions).
 */
const applyCheckerTransparencyEdgeFlood = (data, w, h, P, ox, oy, c0, c1, tol) => {
  const cellCount = w * h;
  const idxAt = (x, y) => (y * w + x) * 4;

  const matchesCheckerCell = (x, y) => {
    const i = idxAt(x, y);
    if (data[i + 3] < 12) return false;
    const parity =
      (Math.floor((x + ox) / P) + Math.floor((y + oy) / P)) & 1;
    const exp = parity === 0 ? c0 : c1;
    return (
      distRgb(data[i], data[i + 1], data[i + 2], exp.r, exp.g, exp.b) < tol
    );
  };

  const seen = new Uint8Array(cellCount);
  const qx = new Int32Array(cellCount);
  const qy = new Int32Array(cellCount);
  let head = 0;
  let tail = 0;

  const push = (x, y) => {
    const ix = y * w + x;
    if (seen[ix]) return;
    if (!matchesCheckerCell(x, y)) return;
    seen[ix] = 1;
    qx[tail] = x;
    qy[tail] = y;
    tail++;
  };

  for (let x = 0; x < w; x++) {
    push(x, 0);
    push(x, h - 1);
  }
  for (let y = 0; y < h; y++) {
    push(0, y);
    push(w - 1, y);
  }

  while (head < tail) {
    const x = qx[head];
    const y = qy[head];
    head++;
    if (x > 0) push(x - 1, y);
    if (x < w - 1) push(x + 1, y);
    if (y > 0) push(x, y - 1);
    if (y < h - 1) push(x, y + 1);
  }

  for (let i = 0; i < cellCount; i++) {
    if (!seen[i]) continue;
    data[i * 4 + 3] = 0;
  }
};

/**
 * If the image looks like a flattened transparency checkerboard, return a PNG blob
 * with those pixels cleared; otherwise return the original blob.
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
export async function stripBakedInCheckerboardFromImageBlob(blob) {
  pasteDebug("stripBakedInCheckerboardFromImageBlob: start", {
    type: blob?.type,
    size: blob?.size,
    hasCreateImageBitmap: typeof createImageBitmap === "function",
  });

  if (!blob || typeof createImageBitmap !== "function") {
    pasteDebug("skip: no blob or createImageBitmap");
    return blob;
  }
  const type = (blob.type || "").toLowerCase();
  // JPEG clipboard captures are common; checker is baked into opaque pixels.
  if (!/^image\/(png|webp|jpeg|jpg)/.test(type)) {
    pasteDebug("skip: MIME not png/webp/jpeg", type);
    return blob;
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch (err) {
    pasteDebug("skip: createImageBitmap failed", err?.message || err);
    return blob;
  }

  const w = bitmap.width;
  const h = bitmap.height;
  if (w * h > MAX_PIXELS_TO_PROCESS) {
    bitmap.close();
    pasteDebug("skip: image too large (pixels)", w * h);
    return blob;
  }

  const analysisScale = Math.min(1, ANALYSIS_MAX_DIM / Math.max(w, h));
  const wS = Math.max(1, Math.round(w * analysisScale));
  const hS = Math.max(1, Math.round(h * analysisScale));

  const analysisCanvas = document.createElement("canvas");
  analysisCanvas.width = wS;
  analysisCanvas.height = hS;
  const aCtx = analysisCanvas.getContext("2d", { willReadFrequently: true });
  if (!aCtx) {
    bitmap.close();
    pasteDebug("skip: no 2d context (analysis)");
    return blob;
  }
  aCtx.drawImage(bitmap, 0, 0, wS, hS);
  let analysisImg;
  try {
    analysisImg = aCtx.getImageData(0, 0, wS, hS);
  } catch (err) {
    bitmap.close();
    pasteDebug("skip: getImageData failed (analysis)", err?.message || err);
    return blob;
  }
  const dataS = analysisImg.data;

  if (fastSkipCheckerboardDetection(dataS, wS, hS)) {
    bitmap.close();
    pasteDebug("strip: skipped after fast corner check (not a baked checker candidate)");
    return blob;
  }

  const { best, perPeriodBest } = findBestCheckerboardFitScaled(
    dataS,
    wS,
    hS,
    w,
    h
  );

  pasteDebug("scan", {
    width: w,
    height: h,
    analysisW: wS,
    analysisH: hS,
    perPeriodTop: perPeriodBest.slice(0, 8),
  });

  if (!best) {
    bitmap.close();
    pasteDebug(
      "no checkerboard model matched corners/border (see per-period scores above if any)"
    );
    return blob;
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    bitmap.close();
    pasteDebug("skip: no 2d context (full)");
    return blob;
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  let imgData;
  try {
    imgData = ctx.getImageData(0, 0, w, h);
  } catch (err) {
    pasteDebug("skip: getImageData failed (full)", err?.message || err);
    return blob;
  }
  const data = imgData.data;

  const pairDist = best.pairDist;
  const isJpeg = /^image\/(jpeg|jpg)$/i.test(type);
  if (isJpeg && pairDist < JPEG_MIN_CHECKER_PAIR_DIST) {
    pasteDebug(
      "skip: JPEG two-tone too weak (likely photo noise, not a UI checker)",
      { pairDist: Number(pairDist.toFixed(2)) }
    );
    return blob;
  }

  const stripTol = Math.min(
    32,
    Math.max(8, Math.round(pairDist * 0.45 + 4))
  );

  const useGlobalStrip =
    best.matchRate >= GLOBAL_STRIP_MIN_MATCH &&
    pairDist >= GLOBAL_STRIP_MIN_PAIR_DIST &&
    best.rmsToCluster <= GLOBAL_STRIP_MAX_RMS;

  pasteDebug("applying strip", {
    P: best.P,
    region: best.region,
    ox: best.ox,
    oy: best.oy,
    mode: useGlobalStrip ? "global" : "edgeFlood",
    matchRate: Number(best.matchRate.toFixed(3)),
    pairDist: Number(pairDist.toFixed(1)),
    rmsToCluster: Number(best.rmsToCluster.toFixed(2)),
    stripTol,
    c0: {
      r: best.c0.r.toFixed(1),
      g: best.c0.g.toFixed(1),
      b: best.c0.b.toFixed(1),
    },
    c1: {
      r: best.c1.r.toFixed(1),
      g: best.c1.g.toFixed(1),
      b: best.c1.b.toFixed(1),
    },
  });

  const stripFn = useGlobalStrip
    ? applyCheckerTransparencyGlobal
    : applyCheckerTransparencyEdgeFlood;
  stripFn(
    data,
    w,
    h,
    best.P,
    best.ox,
    best.oy,
    best.c0,
    best.c1,
    stripTol
  );
  ctx.putImageData(imgData, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob(
      (out) => {
        canvas.width = 0;
        canvas.height = 0;
        const resolved = out && out.size > 0 ? out : blob;
        pasteDebug("toBlob done", {
          changed: resolved !== blob,
          outType: resolved?.type,
          outSize: resolved?.size,
          inSize: blob.size,
        });
        resolve(resolved);
      },
      "image/png",
      undefined
    );
  });
}
